/**
 * Invitations nominatives à voter.
 *
 * Les adresses ne transitent plus par `poll_data_json` : elles vivent dans
 * `public.poll_invitations`, table sans aucun accès direct anon/authenticated.
 * Tout passe par des RPC SECURITY DEFINER qui vérifient la propriété du
 * sondage — y compris pour un créateur invité, via son `guest_owner_token`.
 *
 * Les jetons en clair ne sont renvoyés QU'UNE FOIS, à la création ou à la
 * rotation : seul leur hash est stocké. C'est ce qui garantit qu'une lecture
 * de la base ne fournit aucun lien de vote utilisable.
 */

import { supabase } from "@core/lib/supabase";
import {
  getOwnerTokenForPoll,
  getFingerprint,
} from "@core/lib/guestIdentityService";
import { logger } from "@core/lib/logger";
import { invokeSupabaseFunction } from "@core/lib/supabaseApi";

export interface InvitationToken {
  email: string;
  token: string;
}

export interface InvitationRow {
  id: string;
  email: string;
  status: "pending" | "sent" | "voted" | "revoked";
  sent_at: string | null;
  last_sent_at: string | null;
  send_count: number;
  first_used_at: string | null;
  use_count: number;
  revoked_at: string | null;
  expires_at: string | null;
  has_voted: boolean;
  source?: "group" | "manual";
}

export interface GroupSyncStatus {
  group_id: string;
  group_name: string;
  new_emails: string[];
}

/**
 * Preuve de propriété d'un sondage. `pollId` choisit le jeton du trousseau
 * (lien d'admin ouvert dans ce navigateur) ; sans pollId (appels par
 * identifiant d'invitation), repli sur le jeton principal.
 */
async function ownershipProof(pollId?: string | null): Promise<{
  p_fingerprint: string | null;
  p_owner_token: string | null;
}> {
  let fingerprint: string | null = null;
  try {
    fingerprint = await getFingerprint();
  } catch {
    // Preuve optionnelle : un créateur authentifié n'en a pas besoin.
  }
  return {
    p_fingerprint: fingerprint,
    p_owner_token: getOwnerTokenForPoll(pollId),
  };
}

/**
 * Crée (ou fait tourner) les invitations d'un sondage.
 * Retourne les liens en clair — à afficher immédiatement, ils sont
 * irrécupérables ensuite.
 */
export async function createPollInvitations(
  pollId: string,
  emails: string[],
  opts?: { source?: "group" | "manual"; groupId?: string },
): Promise<InvitationToken[]> {
  const proof = await ownershipProof(pollId);
  const { data, error } = await supabase.rpc("add_poll_invitations", {
    p_poll_id: pollId,
    p_emails: emails,
    ...proof,
    // Champs optionnels : ne les envoyer que renseignés (front/CI peuvent
    // se déployer avant la migration côté base — cf. règle RPC du repo).
    ...(opts?.source ? { p_source: opts.source } : {}),
    ...(opts?.groupId ? { p_group_id: opts.groupId } : {}),
  });
  if (error) {
    logger.error("add_poll_invitations a échoué", "poll", {
      pollId,
      error: error.message,
    });
    throw new Error(error.message);
  }
  return (data ?? []) as InvitationToken[];
}

/** Plafond de destinataires par appel, aligné sur MAX_RECIPIENTS de l'Edge function. */
const SEND_BATCH_SIZE = 50;

export interface SendInvitationsResult {
  successCount: number;
  failedCount: number;
}

/**
 * Envoie un email d'invitation par destinataire, chacun avec SON lien
 * (`?invite=<jeton>`). Appel direct à `send-poll-invitation-email` : cette
 * fonction vérifie la propriété (JWT du créateur, ou guestOwnerToken pour un
 * créateur sans compte), revérifie chaque couple email/jeton contre
 * poll_invitations, puis passe les lignes envoyées en `sent`.
 *
 * Remplace l'ancien passage par `send-poll-invitation-email-proxy`, que le
 * navigateur bloquait (preflight CORS sans `apikey` / `x-client-info`) : aucune
 * invitation ne partait depuis la création d'un sondage (constat 2026-09-15).
 *
 * Ne lève pas : un lot en échec est compté en `failedCount`.
 */
export async function sendPollInvitations(
  pollId: string,
  invitations: InvitationToken[],
): Promise<SendInvitationsResult> {
  const result: SendInvitationsResult = { successCount: 0, failedCount: 0 };
  if (invitations.length === 0) return result;

  const guestOwnerToken = getOwnerTokenForPoll(pollId);
  for (let i = 0; i < invitations.length; i += SEND_BATCH_SIZE) {
    const batch = invitations.slice(i, i + SEND_BATCH_SIZE);
    try {
      const data = await invokeSupabaseFunction<{
        successCount?: number;
        failedCount?: number;
      }>(
        "send-poll-invitation-email",
        {
          pollId,
          invitations: batch,
          ...(guestOwnerToken ? { guestOwnerToken } : {}),
        },
        // Un mail par destinataire côté serveur : 50 envois séquentiels.
        // Créateur sans compte autorisé : son droit est prouvé par
        // guestOwnerToken, vérifié côté serveur.
        { timeout: 60000, requireAuth: false },
      );
      result.successCount += data.successCount ?? 0;
      result.failedCount += data.failedCount ?? 0;
    } catch (error) {
      result.failedCount += batch.length;
      logger.error("Envoi des invitations échoué", "poll", {
        pollId,
        batchSize: batch.length,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return result;
}

/** Statut de synchronisation groupe : membres non encore invités, s'il y en a. */
export async function getPollGroupSyncStatus(
  pollId: string,
): Promise<GroupSyncStatus | null> {
  const proof = await ownershipProof(pollId);
  const { data, error } = await supabase.rpc("get_poll_group_sync_status", {
    p_poll_id: pollId,
    ...proof,
  });
  if (error) throw new Error(error.message);
  const rows = (data ?? []) as GroupSyncStatus[];
  return rows[0] ?? null;
}

/** Compte léger pour le badge de relance (page résultats). */
export async function getPollInvitationsWaitingCount(
  pollId: string,
): Promise<number> {
  const proof = await ownershipProof(pollId);
  const { data, error } = await supabase.rpc(
    "get_poll_invitations_waiting_count",
    { p_poll_id: pollId, ...proof },
  );
  if (error) throw new Error(error.message);
  return (data as number) ?? 0;
}

/** Liste les invitations pour le panneau créateur. */
export async function listPollInvitations(
  pollId: string,
): Promise<InvitationRow[]> {
  const proof = await ownershipProof(pollId);
  const { data, error } = await supabase.rpc("list_poll_invitations", {
    p_poll_id: pollId,
    ...proof,
  });
  if (error) throw new Error(error.message);
  return (data ?? []) as InvitationRow[];
}

/** Désactive un lien sans supprimer la trace de l'invitation. */
export async function revokePollInvitation(
  invitationId: string,
  pollId?: string | null,
): Promise<void> {
  const proof = await ownershipProof(pollId);
  const { error } = await supabase.rpc("revoke_poll_invitation", {
    p_invitation_id: invitationId,
    ...proof,
  });
  if (error) throw new Error(error.message);
}

/** Supprime définitivement une invitation. */
export async function deletePollInvitation(
  invitationId: string,
  pollId?: string | null,
): Promise<void> {
  const proof = await ownershipProof(pollId);
  const { error } = await supabase.rpc("delete_poll_invitation", {
    p_invitation_id: invitationId,
    ...proof,
  });
  if (error) throw new Error(error.message);
}

/** Délai minimal entre deux envois à un même invité (réputation d'envoi). */
export const REMINDER_COOLDOWN_MS = 24 * 60 * 60 * 1000;

/** L'invité a-t-il reçu un mail il y a moins de 24 h ? */
export function isInReminderCooldown(
  row: Pick<InvitationRow, "last_sent_at">,
  now: number = Date.now(),
): boolean {
  return (
    !!row.last_sent_at &&
    now - new Date(row.last_sent_at).getTime() < REMINDER_COOLDOWN_MS
  );
}

export interface ReminderResult extends SendInvitationsResult {
  /** Ignorés côté serveur, par motif. */
  skipped: { cooldown: number; voted: number; revoked: number };
}

/**
 * Relance des invitations existantes avec le MÊME lien (jeton déchiffré côté
 * serveur ; nouveau lien seulement pour une invitation antérieure au
 * chiffrement). Le serveur ignore votants, révoqués et invités relancés il y
 * a moins de 24 h, et ne compte `send_count` que pour un mail réellement parti.
 *
 * Remplace resendPollInvitation : la RPC resend_poll_invitation passait le
 * statut à `sent` sans rien envoyer.
 *
 * Ne lève pas : un lot en échec est compté en `failedCount`.
 */
export async function sendInvitationReminders(
  pollId: string,
  invitationIds: string[],
): Promise<ReminderResult> {
  const result: ReminderResult = {
    successCount: 0,
    failedCount: 0,
    skipped: { cooldown: 0, voted: 0, revoked: 0 },
  };
  if (invitationIds.length === 0) return result;

  const guestOwnerToken = getOwnerTokenForPoll(pollId);
  for (let i = 0; i < invitationIds.length; i += SEND_BATCH_SIZE) {
    const batch = invitationIds.slice(i, i + SEND_BATCH_SIZE);
    try {
      const data = await invokeSupabaseFunction<{
        successCount?: number;
        failedCount?: number;
        skipped?: Partial<ReminderResult["skipped"]>;
      }>(
        "send-poll-invitation-email",
        {
          pollId,
          mode: "reminder",
          invitationIds: batch,
          ...(guestOwnerToken ? { guestOwnerToken } : {}),
        },
        { timeout: 60000, requireAuth: false },
      );
      result.successCount += data.successCount ?? 0;
      result.failedCount += data.failedCount ?? 0;
      result.skipped.cooldown += data.skipped?.cooldown ?? 0;
      result.skipped.voted += data.skipped?.voted ?? 0;
      result.skipped.revoked += data.skipped?.revoked ?? 0;
    } catch (error) {
      result.failedCount += batch.length;
      logger.error("Relance des invitations échouée", "poll", {
        pollId,
        batchSize: batch.length,
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }
  return result;
}

/** Erreur renvoyée par set_poll_auto_reminders sans abonnement Solo. */
export const SOLO_PLAN_REQUIRED = "SOLO_PLAN_REQUIRED";

/**
 * Active ou désactive la relance AUTOMATIQUE des invités non-répondants
 * (J+2, J+5, veille de clôture — 3 relances max par invité).
 *
 * Réservé au plan Solo produit « date » : la RPC lève SOLO_PLAN_REQUIRED si
 * le créateur n'a pas d'abonnement actif. La DÉSACTIVATION reste permise sans
 * abonnement, pour ne pas piéger un créateur dont l'abonnement a expiré.
 */
export async function setPollAutoReminders(
  pollId: string,
  enabled: boolean,
): Promise<boolean> {
  const { data, error } = await supabase.rpc("set_poll_auto_reminders", {
    p_poll_id: pollId,
    p_enabled: enabled,
  });
  if (error) throw new Error(error.message);
  return data === true;
}

/** Relance automatique active sur ce sondage ? (false si illisible). */
export async function getPollAutoReminders(pollId: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("polls")
    .select("auto_reminders_enabled")
    .eq("id", pollId)
    .maybeSingle();
  if (error) {
    logger.warn("Lecture de auto_reminders_enabled échouée", "poll", {
      pollId,
      error: error.message,
    });
    return false;
  }
  return (
    (data as { auto_reminders_enabled?: boolean } | null)
      ?.auto_reminders_enabled === true
  );
}
